import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const PDFViewer = ({ isOpen, onClose, title, pdfFiles }) => {
  const { t } = useTranslation();
  const [activeLanguage, setActiveLanguage] = useState("en");
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileView, setShowMobileView] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (pdfFiles) {
      setActiveLanguage(Object.keys(pdfFiles)[0]);
    }
  }, [pdfFiles]);

  const newsletters = pdfFiles || {
    en: {
      title: "DCA First Newsletter",
      file: "/pdf/DCA_newsletter_english .pdf",
      flag: "🇬🇧",
      language: "English",
    },
    ro: {
      title: "DCA First Newsletter",
      file: "/pdf/DCA_newsletter_romain.pdf",
      flag: "🇷🇴",
      language: "Română",
    },
    ar: {
      title: "DCA First Newsletter",
      file: "/pdf/DCA_newsletter_arabic.pdf",
      flag: "🇸🇦",
      language: "العربية",
    },
  };

  const handleDownload = (file) => {
    const link = document.createElement("a");
    link.href = file;
    link.download = file.split("/").pop();
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-[95%] sm:max-w-[90%] md:max-w-[80%] lg:max-w-[75%] xl:max-w-[65%] max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 10l4.553-2.276A2 2 0 0020 6.382V5a2 2 0 00-2-2H6a2 2 0 00-2 2v1.382a2 2 0 00.447 1.342L9 10m6 0v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4m6 0l-6-3"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold truncate">
                  {title}
                </h2>
                <p className="text-emerald-100 text-sm sm:text-base hidden sm:block">
                  Choose your preferred language
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 flex-shrink-0 ml-2"
            >
              <svg
                className="w-4 h-4 sm:w-6 sm:h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row h-[75vh] sm:h-[80vh]">
          {/* Language Selector - Left Side */}
          <div className="w-full lg:w-72 bg-gray-50 border-r border-gray-200 p-3 sm:p-4 lg:max-h-none max-h-[35vh] overflow-y-auto">
            {/* PDF Header Info */}
            <div className="mb-6 sm:mb-10 p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow-md">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                <span className="text-xl sm:text-2xl">
                  {newsletters[activeLanguage].flag}
                </span>
                <div className="min-w-0 flex-1">
                  <h4 className="text-base sm:text-lg font-bold text-gray-800 truncate">
                    {newsletters[activeLanguage].title}
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {newsletters[activeLanguage].language} Version
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-400 rounded-full"></div>
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full"></div>
                  <span className="ml-2 text-xs sm:text-sm text-gray-500">
                    PDF Viewer
                  </span>
                </div>
                <button
                  onClick={() =>
                    handleDownload(newsletters[activeLanguage].file)
                  }
                  className="flex items-center space-x-1 px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-lg transition-colors duration-200"
                  title="Download PDF"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="hidden sm:inline">Download</span>
                </button>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4">
                Select Language
              </h3>
              {Object.entries(newsletters).map(([key, newsletter]) => (
                <button
                  key={key}
                  onClick={() => setActiveLanguage(key)}
                  className={`w-full flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 ${
                    activeLanguage === key
                      ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg"
                      : "bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 shadow-md hover:shadow-lg"
                  }`}
                >
                  <span className="text-xl sm:text-2xl flex-shrink-0">
                    {newsletter.flag}
                  </span>
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-semibold text-sm sm:text-base truncate">
                      {newsletter.language}
                    </div>
                    <div className="text-xs sm:text-sm opacity-80 truncate">
                      {newsletter.title}
                    </div>
                  </div>
                  {activeLanguage === key && (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse flex-shrink-0"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* PDF Viewer - Right Side */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* PDF Embed */}
            <div className="flex-1 bg-white min-h-[50vh] lg:min-h-0 overflow-y-auto">
              {isMobile ? (
                <div className="min-h-full flex flex-col items-center justify-start p-4 sm:p-6 text-center">
                  <div className="mb-4 sm:mb-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-3 sm:mb-4">
                      <svg
                        className="w-8 h-8 sm:w-10 sm:h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 10l4.553-2.276A2 2 0 0020 6.382V5a2 2 0 00-2-2H6a2 2 0 00-2 2v1.382a2 2 0 00.447 1.342L9 10m6 0v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4m6 0l-6-3"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">
                      {newsletters[activeLanguage].title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-2">
                      For the best viewing experience on mobile, please download
                      the PDF or view it in a new tab.
                    </p>
                  </div>

                  <div className="space-y-3 w-full max-w-xs sm:max-w-sm px-2">
                    <button
                      onClick={() =>
                        handleDownload(newsletters[activeLanguage].file)
                      }
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 sm:px-6 py-3 rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                    >
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm sm:text-base">Download PDF</span>
                    </button>

                    <button
                      onClick={() =>
                        window.open(newsletters[activeLanguage].file, "_blank")
                      }
                      className="w-full bg-white border-2 border-emerald-500 text-emerald-600 px-4 sm:px-6 py-3 rounded-xl font-semibold hover:bg-emerald-50 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                    >
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      <span className="text-sm sm:text-base">
                        Open in New Tab
                      </span>
                    </button>
                  </div>
                </div>
              ) : (
                <iframe
                  src={`${newsletters[activeLanguage].file}#toolbar=1&navpanes=1&scrollbar=1`}
                  className="w-full h-full border-0"
                  title={newsletters[activeLanguage].title}
                  loading="lazy"
                  style={{ minHeight: "400px" }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
